import { h, Component } from 'preact'
// @ts-expect-error   // fix this by defining the mapping to 'downshift' in tsconfig
import Downshift from 'downshift/preact'
import { SearchBoxProvided } from 'react-instantsearch/connectors'
import { insights, AnswerHit, AlgoliaAnswerCallback } from '../../../services/algoliaService'
import HitContainerWithFederated from './HitContainerWithFederated'
import { AllSearchResults, SearchResults, SearchResult, SearchResultWithMetadata, GetItemPropsFn, isMultiIndexResult, Hit, ZendeskHit, EventHit, isCommunityResultEvent, SalesforceHit, SkilljarHit, FreshdeskHit, SearchType, TopicClickedEvent, TopicClickedGoogleAnalyticsEvent, isCommunityResult } from './index'
import { SearchBox, doSearch } from './SearchBox'
import localStorageCache from '../../../utils/localStorageCache'
import Phrase from '../../../libs/translationsUtils/phrase'
import { SEARCH_CONVERSION } from '../../../libs/config'
import userAvatar from '../../../utils/userAvatar'
import TrackingLink from '../../link/tracking-link'
import CSSTransitionGroup from 'preact-css-transition-group'
import Portal from 'preact-portal'
import Icon from '../../assets'
import { SearchQuery } from './Search'
import { ControllerStateAndHelpers } from 'downshift'
import tracker from '../../../components/tracker/publish'
import Analytics from '../../analytics/Analytics'
import WithABTest, { Version } from '../../utils/ABTest'

interface Props {
  version: Version
  refine: SearchBoxProvided['refine']
  updateSearch?: (query: SearchQuery, cb: () => void) => void
  allSearchResults: SearchResults | AllSearchResults | null
  indexName: string
  isAlgoliaAnswersActive: boolean
  getAnswers: (input: string, cb: AlgoliaAnswerCallback) => void
}

const eventTrackerInfo = (hit: AnswerHit|EventHit|Hit|ZendeskHit|SalesforceHit|SkilljarHit|FreshdeskHit, type: SearchType, idx: number) => {
  let resultType = 'Yip.CommunityResultClicked'
  if (type === 'zendesk') {
    resultType = 'Yip.ZendeskGuideResultClicked'
  }
  if (type === 'salesforce') {
    resultType = 'Yip.SalesforceKBResultClicked'
  }
  if (type === 'skilljar') {
    resultType = 'Yip.SkilljarResultClicked'
  }
  if (type === 'freshdesk') {
    resultType = 'Yip.FreshdeskResultClicked'
  }

  const track: TopicClickedEvent = {
    type: resultType,
    topic_url: hit.topic_url ?? '',
    click_source: 'live-search',
    topic_position: idx,
    topic_title: hit.title,
    topic_topic_type: isCommunityResult(hit) ? hit.content_type : type
  }
  if (isCommunityResult(hit) && !isCommunityResultEvent(hit)) {
    track.category_id = hit.forum
    track.category_title = hit.forum_title
  }

  const gaEvents: TopicClickedGoogleAnalyticsEvent = {
    eventCategory: 'Live Search',
    eventAction: 'Topic Clicked',
    eventLabel: {
      Link: hit.topic_url ?? '',
      Position: idx,
      Type: isCommunityResult(hit) ? hit.content_type : type
    }
  }

  return {
    track,
    gaEvents
  }
}

class BasicAlgoliaSearch extends Component<Props> {
  itemToString (i: SearchResult) {
    return i ? i.title : i
  }

  stateReducer (state: {inputValue: string}, changes: {type: string}) {
    switch (changes.type) {
      // prevent closing of the popup on certain actions
      case Downshift.stateChangeTypes.mouseUp:
      case Downshift.stateChangeTypes.blurInput:
      case Downshift.stateChangeTypes.touchStart:
        return {}
        // prevent clicking/selecting items from overwriting inputValue
      case Downshift.stateChangeTypes.keyDownEnter:
      case Downshift.stateChangeTypes.clickItem:
        return {
          ...changes,
          inputValue: state.inputValue,
          highlightedIndex: null
        }
      default:
        return changes
    }
  }

  handleOnChange (item: SearchResultWithMetadata) {
    insights('clickedObjectIDsAfterSearch', {
      objectIDs: [item.objectID],
      eventName: 'Live search item click',
      positions: [item.position],
      queryID: item.queryID,
      index: item.indexName
    })

    let resultType: SearchType = 'community'
    const type = item.indexName.split('-').pop()
    if (type === 'zendesk') resultType = 'zendesk'
    if (type === 'salesforce') resultType = 'salesforce'
    if (type === 'skilljar') resultType = 'skilljar'
    if (type === 'freshdesk') resultType = 'freshdesk'

    const { track, gaEvents } = eventTrackerInfo(item, resultType, item.position)

    tracker.trackEvent('userevent', track)
    Analytics.send(gaEvents.eventCategory, gaEvents.eventAction, gaEvents.eventLabel)

    localStorageCache.setItem(SEARCH_CONVERSION, {
      objectIDs: [item.objectID],
      queryID: item.queryID,
      index: item.indexName
    })

    // user pressed enter to select the item
    if ('KeyboardEvent' in window && window.event instanceof window.KeyboardEvent) {
      if (/^https?/.test(item.topic_url ?? '')) {
        window.open(item.topic_url, '_blank')
      } else {
        window.location.href = item.topic_url ?? item.url ?? ''
      }
    }
  }

  render ({
    version,
    refine,
    updateSearch,
    allSearchResults,
    indexName,
    isAlgoliaAnswersActive
  }: Props) {
    if (typeof window !== 'undefined') isAlgoliaAnswersActive = isAlgoliaAnswersActive || !!window.location.search.match('activateAnswers=yes')
    return (
      <Downshift
        itemToString={this.itemToString}
        stateReducer={this.stateReducer}
        onChange={this.handleOnChange}
      >
        {({
          getInputProps,
          getItemProps,
          highlightedIndex,
          setHighlightedIndex,
          closeMenu,
          openMenu,
          inputValue,
          setState,
          reset,
          isOpen
        }: ControllerStateAndHelpers<SearchResult>) => {
          const isOpenWithAnswersActive = isOpen && isAlgoliaAnswersActive
          const isOpenWithoutAnswersActive = isOpen && !isAlgoliaAnswersActive

          return (
            <div className={isAlgoliaAnswersActive ? '' : 'algolia-search-container'}>

              <div className={`${!inputValue ? 'algolia-search-container--empty-input' : ''}`}>
                <Portal key='backdrop-portal' into='body'>
                  <CSSTransitionGroup transitionName='search-backdrop-transition' transitionEnterTimeout={250} transitionLeaveTimeout={250}>
                    {isOpenWithAnswersActive
                      ? <div onClick={() => closeMenu()} className='algolia-search--opacity' />
                      : null}
                  </CSSTransitionGroup>
                </Portal>

                <Portal key='popup-portal' into='body'>
                  <CSSTransitionGroup transitionName='search-popup-transition' transitionEnterTimeout={250} transitionLeaveTimeout={250}>{isOpenWithAnswersActive
                    ? <div class='algolia-search-container algolia-search-container--open-popup'>

                      <button type='button' className='algolia-search-container__close-button' onClick={(e) => { reset(); e.stopPropagation() }}>
                        <Icon icon='close' />
                      </button>
                      <div class='algolia-search-container__header'>
                        <h3 class='algolia-search-container--title'>
                          <Phrase m='Forum' p='live.search.placeholder' />
                        </h3>
                        <SearchBox
                          reset={() => setState({ inputValue: '' })}
                          refine={refine}
                          updateSearch={updateSearch}
                          setHighlightedIndex={setHighlightedIndex}
                          inputValue={inputValue ?? ''}
                          getInputProps={getInputProps}
                          placeholder='Start typing to see results...'
                          focusOnMount
                          openMenu={openMenu}
                          isOpen={isOpen}
                          key='search'
                        />
                      </div>
                      <div class='algolia-search-container__body'>
                        {version === 'A'
                          ? <AlgoliaAnswer
                              key='answer'
                              query={inputValue ?? ''}
                              getItemProps={getItemProps}
                              highlightedIndex={highlightedIndex}
                              queryID={isMultiIndexResult(allSearchResults)
                                ? allSearchResults?.[indexName]?.queryID
                                : allSearchResults?.queryID}
                              indexName={indexName}
                              getAnswers={this.props.getAnswers}
                            />
                          : null}
                        {inputValue
                          ? <HitContainerWithFederated
                              key='hits-container'
                              allSearchResults={allSearchResults}
                              inputValue={inputValue}
                              doSearch={doSearch}
                              highlightedIndex={highlightedIndex}
                              setHighlightedIndex={setHighlightedIndex}
                              getItemProps={getItemProps}
                              indexName={indexName}
                              previousResultsCount={version === 'A' ? 1 : 0}
                            />
                          : null}
                      </div>
                      {inputValue
                        ? <div class='algolia-search-container__footer'>

                          <TrackingLink
                            gaEvents={{
                              eventCategory: 'Search',
                              eventAction: 'View all clicked',
                              eventLabel: {
                                'Search Query': inputValue
                              }
                            }}
                            track={{
                              type: 'AlgoliaAnswer.ViewAll',
                              click_source: 'live-search-with-answer'
                            }} class='btn btn--primary' icon='search' href={`/search?q=${inputValue}`}
                          >
                            <Phrase p='live.search.view.all' /> {allSearchResults?.nbHits ? <span>({allSearchResults.nbHits})</span> : null}
                          </TrackingLink>
                          <span class='algolia-search-container__footer-text'>Didn't find your answer?</span>
                          <TrackingLink
                            gaEvents={{
                              eventCategory: 'Search',
                              eventAction: 'Ask question clicked',
                              eventLabel: {
                                'Search Query': inputValue
                              }
                            }}
                            track={{
                              type: version === 'A' ? 'Yip.SearchAskQuestionWithAnswer' : 'Yip.SearchAskQuestionNoAnswer',
                              click_source: 'live-search-with-answer'
                            }} class='btn btn--secondary' href='/topic/new'
                          >
                            <Phrase p='live.search.ask.question' />
                          </TrackingLink>
                        </div>
                        : null}
                    </div>
                    : null}</CSSTransitionGroup>
                </Portal>

                {
              isOpenWithoutAnswersActive
                ? <div key='backdrop' onClick={() => closeMenu()} className='algolia-search--opacity' />
                : null
            }
                {isOpenWithAnswersActive
                  ? null
                  : <SearchBox
                      reset={reset}
                      refine={refine}
                      updateSearch={updateSearch}
                      setHighlightedIndex={setHighlightedIndex}
                      inputValue={inputValue ?? ''}
                      getInputProps={getInputProps}
                      openMenu={openMenu}
                      isOpen={isOpen}
                      key='search'
                    />}

                {inputValue && isOpenWithoutAnswersActive
                  ? <HitContainerWithFederated
                      key='hits-container'
                      allSearchResults={allSearchResults}
                      inputValue={inputValue}
                      doSearch={doSearch}
                      highlightedIndex={highlightedIndex}
                      showFooterLinks
                      setHighlightedIndex={setHighlightedIndex}
                      getItemProps={getItemProps}
                      indexName={indexName}
                    />
                  : null}
              </div>
            </div>
          )
        }}
      </Downshift>
    )
  }
}

interface AlgoliaAnswerProps {
  query: string
  getAnswers: (input: string, cb: AlgoliaAnswerCallback) => void
  getItemProps: GetItemPropsFn
  highlightedIndex: number|null
  queryID: string|undefined
  indexName: string
}

interface AlgoliaAnswerState {
  loading: boolean
  hit? : AnswerHit
}

class AlgoliaAnswer extends Component<AlgoliaAnswerProps, AlgoliaAnswerState> {
  constructor () {
    super()
    this.state = {
      loading: false
    }
  }

  componentDidMount () {
    if (this.props.query) this.getAnswer(this.props.query)
  }

  componentDidUpdate (prevProps: AlgoliaAnswerProps) {
    if (this.props.query !== prevProps.query) this.getAnswer(this.props.query)
  }

  getAnswer (input: string) {
    if (!input) return this.setState({ hit: undefined, loading: false })
    if (!this.props.getAnswers) return this.setState({ hit: undefined, loading: false })
    this.setState({ loading: true })
    this.props.getAnswers(input, (hit) => {
      if (input !== this.props.query) return
      if (!hit) return this.setState({ loading: false })
      this.setState({ hit, loading: false })
    })
  }

  render ({ highlightedIndex, getItemProps, queryID, indexName }: AlgoliaAnswerProps, { loading, hit }: AlgoliaAnswerState) {
    if (!hit && !loading) return null
    return (
      <div class='algolia-answer-container'>
        <div key='title' class='algolia-answer algolia-search-results-section__title algolia-search-results-section__title--answer'>
          <span class='algolia-search-results-section__title-label'>Best answer</span>
        </div>
        {loading
          ? <div class='algolia-answer-result algolia-answer-result--loading'><Icon icon='loader' /></div>
          : (hit
              ? <a
                  key={hit.id}
                  href={hit.topic_url ?? hit.url}
                  class={`algolia-answer-result ${highlightedIndex === 0 ? 'algolia-answer-result--highlighted' : ''}`}
                  {...getItemProps({ item: { ...hit, queryID: queryID ?? '', indexName, position: 1 }, index: 0 })}
                  onMouseUp={(e: Event) => e.stopPropagation()}
                >
                <p class='algolia-answer-result__content' dangerouslySetInnerHTML={{ __html: hit._answer.extract.replace(/\[[^>]+\]/ig, '').replace(/ +/g, ' ').replace(/:[a-z0-9_]+:/g, '') }} />
                <h4>{hit.title}</h4>
                {hit.username
                  ? <div class='algolia-answer-result__user-info'>
                    {userAvatar.isDefaultAvatar(hit.author_avatar)
                      ? <div class={`algolia-answer-result__user-info-avatar default-avatar ${userAvatar.getAvatarClass(hit.userid) as string}`}>
                        {userAvatar.getAvatarInital(hit.username)}
                      </div>
                      : <img src={hit.author_avatar} class='algolia-answer-result__user-info-avatar' alt={hit.username} />}
                    <span class='algolia-answer-result__user-info-name'>
                      {hit.username}
                    </span>
                  </div>
                  : null}
              </a>
              : null)}
      </div>
    )
  }
}

const AlgoliaSearch = WithABTest(BasicAlgoliaSearch, 'AlgoliaAnswers')

export default AlgoliaSearch
